import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { User, Session } from '@supabase/supabase-js'
import { UserProfile } from '../types'
import { logUserAction } from '../services/userLogs'

interface AuthState {
  user: User | null
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  error: string | null
}

interface AuthResult {
  success: boolean
  error?: string
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    loading: true,
    error: null
  })

  // Carregar perfil do usuário
  const loadProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          user_group:user_groups(*)
        `)
        .eq('id', userId)
        .single()

      if (error) throw error

      return data
    } catch (error) {
      console.error('Erro ao carregar perfil:', error)
      return null
    }
  }, [])

  // Verificar se o usuário está ativo
  const checkUserActive = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_active')
        .eq('id', userId)
        .single()

      if (error) throw error

      return data?.is_active || false
    } catch (error) {
      console.error('Erro ao verificar status do usuário:', error)
      return false
    }
  }, [])

  // Atualizar último login
  const updateLastLogin = useCallback(async (userId: string) => {
    try {
      await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId)
    } catch (error) {
      console.error('Erro ao atualizar último login:', error)
    }
  }, [])

  // Inicializar autenticação
  useEffect(() => {
    let mounted = true

    const initializeAuth = async () => {
      try {
        // Usar apenas o Supabase padrão, sem customizações
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (!mounted) return
        
        if (sessionError) {
          setAuthState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: sessionError.message
          })
          return
        }

        if (session?.user) {
          // Carregar perfil em background
          const profile = await loadProfile(session.user.id)
          
          if (!mounted) return

          setAuthState({
            user: session.user,
            profile,
            session,
            loading: false,
            error: null
          })
        } else {
          setAuthState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: null
          })
        }
      } catch (error) {
        if (!mounted) return
        
        setAuthState({
          user: null,
          profile: null,
          session: null,
          loading: false,
          error: (error as Error).message
        })
      }
    }

    initializeAuth()

    return () => {
      mounted = false
    }

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        
        if (event === 'SIGNED_IN' && session?.user) {
          // Verificar se o usuário está ativo
          const isActive = await checkUserActive(session.user.id)
          
          if (!isActive) {
            // Se o usuário não está ativo, limpar a sessão e não mostrar erro
            await supabase.auth.signOut()
            setAuthState({
              user: null,
              profile: null,
              session: null,
              loading: false,
              error: null
            })
            return
          }

          // Carregar perfil
          const profile = await loadProfile(session.user.id)
          
          // Atualizar último login
          await updateLastLogin(session.user.id)

          setAuthState({
            user: session.user,
            profile,
            session,
            loading: false,
            error: null
          })
        } else if (event === 'SIGNED_OUT') {
          setAuthState({
            user: null,
            profile: null,
            session: null,
            loading: false,
            error: null
          })
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [loadProfile, checkUserActive, updateLastLogin])

  // Login
  const signIn = async (email: string, password: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

              if (data.user) {
          // Verificar se o usuário está ativo
          const isActive = await checkUserActive(data.user.id)
          
          if (!isActive) {
            setAuthState(prev => ({
              ...prev,
              loading: false,
              error: 'Usuário inativo. Entre em contato com o administrador.'
            }))
            return { success: false, error: 'Usuário inativo. Entre em contato com o administrador.' }
          }

          // Carregar perfil
          const profile = await loadProfile(data.user.id)
        
        // Atualizar último login
        await updateLastLogin(data.user.id)

        // Registrar log de login
        try {
          await logUserAction({
            action: 'login',
            entityType: 'other',
            entityId: null,
            description: `Login realizado no sistema`,
            userId: data.user.id,
          })
        } catch (logError) {
          // Não bloquear o login em caso de erro no log
          console.warn('Erro ao registrar log de login:', logError)
        }

        setAuthState({
          user: data.user,
          profile,
          session: data.session,
          loading: false,
          error: null
        })

        return { success: true }
      }

      return { success: false, error: 'Erro no login' }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Registro
  const signUp = async (email: string, password: string, name?: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name
          }
        }
      })

      if (error) throw error

      if (data.user) {
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: null
        }))

        return { 
          success: true, 
          error: 'Usuário registrado com sucesso! Aguarde a ativação por um administrador.' 
        }
      }

      return { success: false, error: 'Erro no registro' }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Logout
  const signOut = async (): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      // Capturar user_id antes do logout para registrar o log
      const userIdBeforeLogout = authState.user?.id

      // Limpar cache de permissões antes do logout
      if (authState.user) {
        try {
          sessionStorage.removeItem(`permissions_${authState.user.id}`)
        } catch (error) {
          // Erro silencioso ao limpar cache
        }
      }

      const { error } = await supabase.auth.signOut()

      if (error) throw error

      // Registrar log de logout (antes de limpar o estado)
      if (userIdBeforeLogout) {
        try {
          await logUserAction({
            action: 'logout',
            entityType: 'other',
            entityId: null,
            description: `Logout realizado no sistema`,
            userId: userIdBeforeLogout,
          })
        } catch (logError) {
          // Não bloquear o logout em caso de erro no log
          console.warn('Erro ao registrar log de logout:', logError)
        }
      }

      setAuthState({
        user: null,
        profile: null,
        session: null,
        loading: false,
        error: null
      })

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Reset de senha
  const resetPassword = async (email: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      setAuthState(prev => ({ ...prev, loading: false, error: null }))

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Atualizar senha
  const updatePassword = async (password: string): Promise<AuthResult> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) throw error

      setAuthState(prev => ({ ...prev, loading: false, error: null }))

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Atualizar perfil
  const updateProfile = async (profileData: Partial<UserProfile>): Promise<AuthResult> => {
    try {
      if (!authState.user) {
        return { success: false, error: 'Usuário não autenticado' }
      }

      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', authState.user.id)
        .select()
        .single()

      if (error) throw error

      setAuthState(prev => ({
        ...prev,
        profile: data,
        loading: false,
        error: null
      }))

      return { success: true }
    } catch (error) {
      const errorMessage = (error as Error).message
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }))
      return { success: false, error: errorMessage }
    }
  }

  // Verificar se o usuário é admin
  const isAdmin = useCallback(() => {
    return authState.profile?.user_group?.is_admin || false
  }, [authState.profile])

  // Verificar se o usuário está ativo
  const isActive = useCallback(() => {
    return authState.profile?.is_active || false
  }, [authState.profile])

  return {
    user: authState.user,
    profile: authState.profile,
    session: authState.session,
    loading: authState.loading,
    error: authState.error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    isAdmin,
    isActive
  }
}
